'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsAddPhotoClicked from '@alltrails/amplitude/events/logTrailDetailsAddPhotoClicked';
import logTrailDetailsHeroImageClicked from '@alltrails/amplitude/events/logTrailDetailsHeroImageClicked';
import logTrailDetailsTourButtonClicked from '@alltrails/amplitude/events/logTrailDetailsTourButtonClicked';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import useUser from '@alltrails/context/hooks/useUser';
import Image from '@alltrails/core/components/Image';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import type TrailDetails from '@alltrails/types/TrailDetails';
import getStaticMapUrl from '@alltrails/core/utils/getStaticMapUrl';
import IconButton from '@alltrails/denali/components/IconButton';
import Toast from '@alltrails/denali/components/Toast';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Expand from '@alltrails/denali/icons/Expand';
import { handleClosePhotoTour, handleOpenPhotoTour } from '@alltrails/photo-tour';
import { getTrailLocationName, TrailPage, type TrailPhotoLocationInfo, useTrailPhotos } from '@alltrails/trails';
import type { TrailId } from '@alltrails/types/TrailId';
import type TrailPhoto from '@alltrails/types/TrailPhoto';
import useIsTrailMapOpen from '@/hooks/useIsTrailMapOpen';
import useTrailDefaultImage from '@/hooks/useTrailDefaultImage';
import HeroImage from '../HeroImage/HeroImage';
import layoutStyles from '../layouts/layoutStyles.module.scss';
import AddPhotoButton from './AddPhotoButton';
import styles from './DesktopHeroGrid.module.scss';
import PhotosButton from './PhotosButton';
import PhotoTourButton from './PhotoTourButton';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const TrailLightbox = dynamic(() => import('@alltrails/trails').then(module => ({ default: module.TrailLightbox })));
const TrailMap = dynamic(() => import('../TrailMap').then(module => ({ default: module.default })), { ssr: false });
const PhotoTourModal = dynamic(() => import('@alltrails/photo-tour').then(module => ({ default: module.PhotoTourModal })), { ssr: false });
const PhotoUploadModal = dynamic(() => import('@/components/PhotoUploadModal/PhotoUploadModalWrapper'), { ssr: false });

const messages = defineMessages({
  HERO_BUTTON: { defaultMessage: 'Show trail photo {index} of {photoCount, number}' },
  PHOTOS_BUTTON: { defaultMessage: '{photoCount, plural, one {# photo} other {# photos}}' },
  EXPAND_MAP: { defaultMessage: 'Expand map' }
});

type DesktopHeroGridProps = {
  defaultPhoto?: TrailPhoto;
  mapId: number;
  photoCount?: number;
  photoPreviewId?: string;
  trailInfo: TrailPhotoLocationInfo & { id: TrailId };
  length?: number;
  hasPhotoTour: boolean;
  isBot?: boolean;
  trailDetails: TrailDetails;
  trailPage: TrailPage;
};

const DesktopHeroGrid = ({
  defaultPhoto,
  mapId,
  photoCount = 0,
  photoPreviewId,
  trailInfo,
  length,
  hasPhotoTour,
  isBot = false,
  trailDetails,
  trailPage
}: DesktopHeroGridProps) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const dispatch = useDispatch();
  const publicBaseUrl = usePublicBaseUrl();
  const user = useUser();
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [signUpModalIndex, setSignUpModalIndex] = useState(-1);
  const [photoTourModalIndex, setPhotoTourModalIndex] = useState(-1);
  const [signUpModalTrigger, setSignUpModalTrigger] = useState<CarouselDisplayTrigger>('View Trail Photos');
  const [upsellTrigger, setUpsellTrigger] = useState<UpsellTrigger>('View Trail Photos');
  const { isTrailMapOpen, openTrailMap } = useIsTrailMapOpen();
  const [photoUploadModalIndex, setPhotoUploadModalIndex] = useState(-1);
  const [isPhotoToastOpen, setIsPhotoToastOpen] = useState(false);

  const { lightboxPhotos, loadPhotos, loadState, secondPhoto } = useTrailPhotos({ defaultPhoto, trailId: trailInfo.id });
  const { defaultPhotoAlt, defaultPhotoUrl } = useTrailDefaultImage({
    defaultPhoto,
    mapId,
    photoPreviewId,
    photoSize: '3520x1600',
    trailId: trailInfo.id,
    trailPhotoLocationInfo: trailInfo,
    useSatelliteStaticMap: true,
    trailDetails
  });
  const { id } = trailInfo;
  const staticMapUrl = getStaticMapUrl({ baseUrl: publicBaseUrl, id: mapId, scale: 2, size: '368x182', type: 'maps' });
  const staticMapTallUrl = getStaticMapUrl({ baseUrl: publicBaseUrl, id: mapId, scale: 2, size: '368x372', type: 'maps' });

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const handleOpenTour = useCallback(() => {
    dispatch(
      handleOpenPhotoTour({
        tourType: 'trail',
        tourArgs: { trailId: id, isMobile }
      })
    );
    setPhotoTourModalIndex(0);
  }, [dispatch, id, isMobile]);

  const onPhotoClick = useCallback(
    (index: number) => {
      logTrailDetailsHeroImageClicked({ trail_id: id });

      if (!user) {
        setSignUpModalTrigger('View Trail Photos');
        setUpsellTrigger('View Trail Photos');
        setSignUpModalIndex(index);
      } else {
        setLightboxIndex(index);
      }
    },
    [id, user]
  );

  const onMapFallbackClick = useCallback(
    (isExpandButtonClicked?: boolean) => {
      logTrailDetailsActionTapped({
        trail_id: id.toString(),
        trail_page_action: 'open map',
        trail_page_location: isExpandButtonClicked ? 'hero image expand button' : 'hero image inset'
      });
      if (!user) {
        setSignUpModalTrigger('View Static Map');
        setUpsellTrigger('View Static Map');
        setSignUpModalIndex(0);
        return;
      }
      openTrailMap();
    },
    [id, openTrailMap, user]
  );

  const hasExpandButton = (loadState !== 'loaded' && photoCount > 2) || (loadState === 'loaded' && lightboxPhotos.length > 2) || defaultPhoto;

  function getSecondPhotoAltText() {
    if (trailDetails.area?.name) {
      return intl.formatMessage({ defaultMessage: 'Second photo of {name} in {park}' }, { name: trailInfo.name, park: trailDetails.area.name });
    } else if (trailDetails.location?.city) {
      return intl.formatMessage(
        { defaultMessage: 'Second photo of {name} near {cityRegionAndCountry}' },
        { name: trailInfo.name, cityRegionAndCountry: getTrailLocationName(trailDetails) }
      );
    } else if (trailDetails.location?.regionName) {
      return intl.formatMessage(
        { defaultMessage: 'Second photo of {name} in {region}' },
        { name: trailInfo.name, region: trailDetails.location.regionName }
      );
    }

    return intl.formatMessage({ defaultMessage: 'Second photo of {name}' }, { name: trailInfo.name });
  }

  const mapAriaLabel = intl.formatMessage({ defaultMessage: `{name} map` }, { name: trailInfo.name });
  const gridContents = useMemo(() => {
    if ((loadState !== 'loaded' && photoCount > 2) || (loadState === 'loaded' && lightboxPhotos.length > 2)) {
      return (
        <>
          <button
            aria-label={intl.formatMessage(messages.HERO_BUTTON, { index: 1, photoCount })}
            className={classNames(layoutStyles.col4, styles.imageContainer, styles.primaryImage, styles.scrim)}
            onClick={() => onPhotoClick(0)}
          >
            <Image
              alt={defaultPhotoAlt}
              className={styles.image}
              fill
              isLcp
              /**
               * Responsive image sizing calculation for 4-column width in 6-column grid
               *
               * Breakpoint: 1216px = $breakpoint-md-size + 2 * $breakpoint-md-margin
               *
               * Below 1216px (dynamic viewport-based):
               *   Full: 4 * (((100vw - (2 * var(--breakpoint-md-margin))) - (5 * var(--space-100))) / 6) + (3 * var(--space-100))
               *   Simplified: calc(200vw / 3 - 25rem / 6)
               *
               * Above 1216px (fixed container width):
               *   Full: 4 * ((var(--breakpoint-md-size) - (5 * var(--space-100))) / 6) + (3 * var(--space-100))
               *   Simplified: 744px
               *
               * Note: Using separate conditions instead of min/max due to Safari compatibility issues
               */
              sizes="(max-width: 1216px) calc(200vw / 3 - 25rem / 6), 744px"
              src={defaultPhotoUrl}
              title={defaultPhotoAlt}
            />
          </button>
          <div className={classNames(layoutStyles.col2, styles.verticalImages)}>
            {lightboxPhotos.length > 1 ? (
              <button
                aria-label={intl.formatMessage(messages.HERO_BUTTON, { index: 2, photoCount })}
                className={classNames(styles.imageContainer, styles.secondaryImage)}
                onClick={() => onPhotoClick(1)}
              >
                <Image
                  alt={getSecondPhotoAltText()}
                  className={styles.image}
                  handleLoadingState
                  fill
                  /**
                   * Responsive image sizing calculation for 2-column width in 6-column grid
                   *
                   * Breakpoint: 1216px = $breakpoint-md-size + 2 * $breakpoint-md-margin
                   *
                   * Below 1216px (dynamic viewport-based):
                   *   Full: 2 * (((100vw - (2 * var(--breakpoint-md-margin))) - (5 * var(--space-100))) / 6) + (3 * var(--space-100))
                   *   Simplified: calc((200vw - 17rem) / 6 + 1.5rem)
                   *
                   * Above 1216px (fixed container width):
                   *   Full: 2 * ((var(--breakpoint-md-size) - (5 * var(--space-100))) / 6) + (3 * var(--space-100))
                   *   Simplified: 372px
                   *
                   * Note: Using separate conditions instead of min/max due to Safari compatibility issues
                   */
                  sizes="(max-width: 1216px) calc((200vw - 17rem) / 6 + 1.5rem), 372px"
                  src={lightboxPhotos[1].imgUrl}
                />
              </button>
            ) : (
              <ShimmerContainer className={classNames(styles.imageContainer, styles.secondaryImage, styles.placeholderImage)}>
                {null}
              </ShimmerContainer>
            )}
            {!isBot && (
              <button
                aria-label={mapAriaLabel}
                className={classNames(styles.imageContainer, styles.secondaryImage)}
                onClick={() => onMapFallbackClick(false)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    onMapFallbackClick(false);
                  }
                }}
              >
                <Image
                  alt={mapAriaLabel}
                  fill
                  loading="eager"
                  /**
                   * Responsive image sizing calculation for 2-column width in 6-column grid
                   *
                   * Breakpoint: 1216px = $breakpoint-md-size + 2 * $breakpoint-md-margin
                   *
                   * Below 1216px (dynamic viewport-based):
                   *   Full: 2 * (((100vw - (2 * var(--breakpoint-md-margin))) - (5 * var(--space-100))) / 6) + (3 * var(--space-100))
                   *   Simplified: calc((200vw - 17rem) / 6 + 1.5rem)
                   *
                   * Above 1216px (fixed container width):
                   *   Full: 2 * ((var(--breakpoint-md-size) - (5 * var(--space-100))) / 6) + (3 * var(--space-100))
                   *   Simplified: 372px
                   *
                   * Note: Using separate conditions instead of min/max due to Safari compatibility issues
                   */
                  sizes="(max-width: 1216px) calc((200vw - 17rem) / 6 + 1.5rem), 372px"
                  src={staticMapUrl}
                  title={mapAriaLabel}
                />
              </button>
            )}
          </div>
        </>
      );
    } else if (defaultPhoto) {
      return (
        <>
          <button
            aria-label={intl.formatMessage(messages.HERO_BUTTON, { index: 1, photoCount })}
            className={classNames(layoutStyles.col4, styles.imageContainer, styles.primaryImage, styles.scrim)}
            onClick={() => onPhotoClick(0)}
          >
            <HeroImage alt={defaultPhotoAlt} isLcp photoUrl={defaultPhotoUrl} />
          </button>
          {!isBot && (
            <button
              aria-label={mapAriaLabel}
              className={classNames(layoutStyles.col2, styles.imageContainer, styles.secondaryImage)}
              onClick={() => onMapFallbackClick(false)}
            >
              <HeroImage alt={mapAriaLabel} photoUrl={staticMapTallUrl} smallGridImage />
            </button>
          )}
        </>
      );
    } else {
      return (
        !isBot && (
          <button
            className={classNames(layoutStyles.col6, styles.imageContainer, styles.singleImage)}
            aria-label={mapAriaLabel}
            data-testid="static-map-hero"
            onClick={() => onMapFallbackClick(false)}
          >
            <HeroImage alt={defaultPhotoAlt} isLcp photoUrl={defaultPhotoUrl} />
          </button>
        )
      );
    }
  }, [
    loadState,
    photoCount,
    lightboxPhotos,
    defaultPhoto,
    intl,
    defaultPhotoAlt,
    defaultPhotoUrl,
    isBot,
    mapAriaLabel,
    staticMapUrl,
    onPhotoClick,
    onMapFallbackClick,
    staticMapTallUrl
  ]);

  return (
    <>
      <div className={classNames(layoutStyles.colLayout, styles.container)}>
        {gridContents}
        {(!!photoCount || hasPhotoTour) && (
          <div className={styles.photoButtonContainer}>
            {hasPhotoTour && (
              <PhotoTourButton
                isMobile={isMobile}
                onClick={() => {
                  logTrailDetailsTourButtonClicked({ trail_id: id, web_location: 'gallery' });
                  if (!user) {
                    setSignUpModalTrigger('Gallery Tour');
                    setUpsellTrigger('Gallery Tour');
                    setSignUpModalIndex(0);
                  } else {
                    handleOpenTour();
                  }
                }}
              />
            )}
            {!!photoCount && <PhotosButton photoCount={photoCount} trailId={id} trailSlug={trailInfo.slug} />}
          </div>
        )}
        {photoCount === 0 && (
          <AddPhotoButton
            handleClick={() => {
              logTrailDetailsAddPhotoClicked({
                trail_id: id
              });

              if (!user) {
                setSignUpModalTrigger('Add Photo to Trail');
                setUpsellTrigger('Add Photo to Trail');
                setSignUpModalIndex(0);
              } else {
                setPhotoUploadModalIndex(0);
              }
            }}
          />
        )}
        {hasExpandButton && (
          <div className={styles.expandButtonContainer}>
            <IconButton
              icon={{ Component: Expand }}
              size="md"
              variant="elevated"
              onClick={e => {
                e.stopPropagation();
                onMapFallbackClick(true);
              }}
              testId="expand-map"
              title={intl.formatMessage(messages.EXPAND_MAP)}
            />
          </div>
        )}
      </div>
      {lightboxIndex > -1 && (
        <TrailLightbox
          closeModal={() => setLightboxIndex(-1)}
          trailId={id}
          trailSlug={trailInfo.slug}
          lightboxPhotos={lightboxPhotos}
          startingIndex={lightboxIndex}
        />
      )}
      {isTrailMapOpen && <TrailMap isMobile={isMobile} trailName={trailInfo.name} trailDetails={trailDetails} trailPage={trailPage} />}
      {signUpModalIndex > -1 && (
        <SignUpModal
          isOpen
          closeModal={() => {
            setSignUpModalTrigger('View Trail Photos');
            setSignUpModalIndex(-1);
          }}
          onSuccess={() => {
            if (signUpModalTrigger === 'Gallery Tour') {
              handleOpenTour();
            } else if (signUpModalTrigger === 'View Static Map') {
              openTrailMap();
            } else if (signUpModalTrigger === 'Add Photo to Trail') {
              setPhotoUploadModalIndex(0);
            } else if (photoCount || defaultPhoto) {
              setLightboxIndex(signUpModalIndex);
            }
            setSignUpModalIndex(-1);
          }}
          trigger={signUpModalTrigger}
          upsellTrigger={upsellTrigger}
        />
      )}
      {loadState === 'loaded' && photoTourModalIndex > -1 && (
        <PhotoTourModal
          trailId={id}
          totalDistance={length}
          segmentActualDistances={length ? [length] : undefined}
          onClose={() => {
            setPhotoTourModalIndex(-1);
            dispatch(handleClosePhotoTour());
          }}
          defaultPhoto={defaultPhoto}
          secondPhoto={secondPhoto}
          name={trailInfo.name}
          hidePageScrollbarGutter
        />
      )}
      {photoUploadModalIndex > -1 && (
        <PhotoUploadModal
          closeModal={() => setPhotoUploadModalIndex(-1)}
          trailId={id}
          onPhotosAdd={() => setIsPhotoToastOpen(true)}
          shouldHideSuccess
        />
      )}
      {isPhotoToastOpen && (
        <Toast
          isOpen={isPhotoToastOpen}
          message={<FormattedMessage defaultMessage="Success! Your photo will be available soon." />}
          type="neutral"
          timeout={3000}
          setIsOpen={setIsPhotoToastOpen}
          position="alternative"
        />
      )}
    </>
  );
};

export default DesktopHeroGrid;
